import {
  useCallback,
  useEffect,
} from 'react';

import { useSelector } from 'react-redux';

import { Auth } from '../../constants';
import { authSlice } from '../../slices/auth';
import { usePutLinkAccountAuthenticationMutation } from '../../slices/auth/putLinkAccountsAuthApi';

import {
  usePostAuthenticationVerify,
  UsePostAuthenticationVerifyParams,
} from './usePostAuthenticationVerify';

type UsePostAuthenticationVerifyUpgradeAndLinkSocial = UsePostAuthenticationVerifyParams & {
  facebookAccessToken?: string | null;
  googleIdToken?: string | null;
  appleIdToken?: string | null;
};
type UsePostAuthenticationVerifyUpgradeAndLinkSocialProps = {
  userRoleType?: string;
};
// Hook composition = We can't do async/await due to the nature of "epics" (observables)

export const usePostAuthenticationVerifyUpgradeAndLinkSocial = ({
  userRoleType,
}: UsePostAuthenticationVerifyUpgradeAndLinkSocialProps) => {
  const {
    execute: verifyAuthentication,
    error: postAuthenticationVerifyError,
    errorMessage: postAuthenticationVerifyErrorMessage,
    isSuccess: isPostAuthenticationVerifySuccess,
    isLoading: isPostAuthenticationVerifyLoading,
    reset: resetPostAuthenticationVerify,
  } = usePostAuthenticationVerify();

  const socialAuthProviderToLink = useSelector(authSlice.selectors.getSocialAuthProviderToLink);
  const facebookAccessToken = useSelector(authSlice.selectors.getFacebookAccessToken);
  const googleIdToken = useSelector(authSlice.selectors.getGoogleIdToken);
  const appleIdToken = useSelector(authSlice.selectors.getAppleIdToken);

  const [
    executePutLinkAccountAuthentication,
    {
      isLoading: putLinkAccountAuthenticationMutationIsLoading,
      isError: putLinkAccountAuthenticationMutationIsError,
      error: putLinkAccountAuthenticationMutationErrorMessage,
      isSuccess: putLinkAccountAuthenticationMutationSuccess,
    },
  ] = usePutLinkAccountAuthenticationMutation();

  const execute = (requestData: UsePostAuthenticationVerifyUpgradeAndLinkSocial) => {
    verifyAuthentication(requestData);
  };

  const reset = () => {
    resetPostAuthenticationVerify();
  };

  // link social auth provider
  useEffect(() => {
    if (userRoleType !== Auth.RoleType.Owner) {
      return;
    }
    if (!isPostAuthenticationVerifySuccess || !socialAuthProviderToLink) {
      return;
    }
    // Link social login if it has been attempted but an account already exists under the email
    executePutLinkAccountAuthentication(
      {
        socialAuthProviderToLink,
        facebookAccessToken,
        googleIdToken,
        appleIdToken,
      },
    );
  }, [
    userRoleType,
    socialAuthProviderToLink,
    executePutLinkAccountAuthentication,
    isPostAuthenticationVerifySuccess,
    facebookAccessToken,
    googleIdToken,
    appleIdToken,
  ]);

  return {
    error: postAuthenticationVerifyError || putLinkAccountAuthenticationMutationIsError,
    errorMessage: postAuthenticationVerifyErrorMessage
      || putLinkAccountAuthenticationMutationErrorMessage,
    isLoading: isPostAuthenticationVerifyLoading || putLinkAccountAuthenticationMutationIsLoading,

    /*
    There are two scenarios where this is a successful hook call depending on
    whether we have initally signed in with a social auth provider or not
    */

    isSuccess: putLinkAccountAuthenticationMutationSuccess || isPostAuthenticationVerifySuccess,
    isLinkedAccountsSuccess: putLinkAccountAuthenticationMutationSuccess,
    execute,
    reset: useCallback(reset, [resetPostAuthenticationVerify]),
  };
};
